import { Button, Chip, Group, Paper, Stack, Text, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';

/**
 * When something applies: Happy Hour, breakfast until 10:30, a price list that only runs in
 * high season.
 *
 * This is the same stored shape behind three different questions — a price list's window, a menu
 * item's availability, and a discount's "only at certain times" rule — and all three used to be a
 * JSON box. The owner was asked to know that Sunday is 0, that the key is `daysOfWeek`, and that
 * the whole thing is wrapped in `{"rules": …}`. Nothing on any screen taught him that, so the
 * realistic outcome was Happy Hour never being scheduled at all.
 *
 * Several rules, because "and also on Tuesdays" is a second rule rather than a more complicated
 * first one — the engine treats a schedule as active when ANY rule matches.
 */

export interface ScheduleRule {
  daysOfWeek?: number[] | null;
  startTime?: string | null;
  endTime?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export interface Schedule {
  rules: ScheduleRule[];
}

/**
 * Sunday first and numbered from 0, matching `engine/time.ts`. The numbers never reach the screen;
 * they are the reason this component exists.
 *
 * Exported because `WeekdaysField` asks the same question about a different column — the days a
 * vendor's truck comes — and a second copy of "Sunday is 0" is a defect waiting for whoever
 * writes the third one.
 */
export const DAYS = [
  { value: 0, short: 'Sun' },
  { value: 1, short: 'Mon' },
  { value: 2, short: 'Tue' },
  { value: 3, short: 'Wed' },
  { value: 4, short: 'Thu' },
  { value: 5, short: 'Fri' },
  { value: 6, short: 'Sat' },
];

/** Tolerates anything the column might hold, including the bare array an older row may carry. */
export function toRules(value: unknown): ScheduleRule[] {
  if (Array.isArray(value)) return value as ScheduleRule[];
  const rules = (value as Schedule | null | undefined)?.rules;
  return Array.isArray(rules) ? rules : [];
}

/**
 * Empty means "always", which the engine already treats as always-active — so an empty editor must
 * store null rather than `{rules: []}`. The difference is invisible on screen and total in effect.
 */
export function fromRules(rules: ScheduleRule[]): Schedule | null {
  const kept = rules.filter(
    (r) => (r.daysOfWeek?.length ?? 0) > 0 || r.startTime || r.endTime || r.startDate || r.endDate,
  );
  return kept.length > 0 ? { rules: kept } : null;
}

/**
 * The same question the server's `parseHm` answers, asked without the throw. A regex is not enough
 * and never was: `/^\d{1,2}:\d{2}$/` admits "99:99", which the engine still refuses.
 *
 * Mirrors `backend/src/engine/schedule.ts#isHm`.
 */
export function isHm(value: string): boolean {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return false;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return !(h > 24 || min > 59 || (h === 24 && min !== 0));
}

/** A calendar day, as the schedule columns store one. */
export function isPlainDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  // island-date-ok: this round-trips a SUPPLIED date at UTC midnight to catch 2026-02-30. No clock
  // is read, so the answer is the same in every timezone, which is the point of the check.
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/** Mirrors `backend/src/engine/schedule.ts#ruleIsReadable` — what the engine can evaluate at all. */
export function ruleIsReadable(rule: ScheduleRule): boolean {
  if (rule.startTime && !isHm(rule.startTime)) return false;
  if (rule.endTime && !isHm(rule.endTime)) return false;
  if (rule.startDate && !isPlainDate(rule.startDate)) return false;
  if (rule.endDate && !isPlainDate(rule.endDate)) return false;
  if (rule.daysOfWeek?.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) return false;
  return true;
}

/** "Mon–Fri, 07:00–10:30" — the sentence a manager would say out loud. */
export function describeRule(rule: ScheduleRule): string {
  // A rule the engine cannot read grants nothing, so a sentence that renders it as a tidy window is
  // the most misleading thing this function could do. It says what is actually true instead.
  if (!ruleIsReadable(rule)) return 'a rule nobody can read, so it never applies';
  const days = rule.daysOfWeek ?? [];
  const when =
    days.length === 0 || days.length === 7
      ? 'Every day'
      : DAYS.filter((d) => days.includes(d.value))
          .map((d) => d.short)
          .join(', ');
  const from = rule.startTime || '00:00';
  const to = rule.endTime || '24:00';
  const times = rule.startTime || rule.endTime ? `, ${from}–${to}` : ', all day';
  const dates =
    rule.startDate || rule.endDate
      ? ` (${rule.startDate ?? 'any date'} to ${rule.endDate ?? 'any date'})`
      : '';
  return `${when}${times}${dates}`;
}

export function describeSchedule(value: unknown): string {
  const rules = toRules(value);
  if (rules.length === 0) return 'Always';
  return rules.map(describeRule).join('; ');
}

export function ScheduleField({
  label,
  description,
  value,
  onChange,
  emptyMeans = 'Always on.',
}: {
  label: string;
  description?: string;
  value: unknown;
  onChange: (value: Schedule | null) => void;
  /** What no rules at all means here — "always on" for a price list, "always available" for a dish. */
  emptyMeans?: string;
}) {
  const rules = toRules(value);
  const set = (next: ScheduleRule[]) => onChange(fromRules(next));
  const patch = (index: number, change: Partial<ScheduleRule>) =>
    set(rules.map((r, i) => (i === index ? { ...r, ...change } : r)));

  return (
    <div>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      {description && (
        <Text size="xs" c="dimmed" mb={6}>
          {description}
        </Text>
      )}
      <Stack gap="xs">
        {rules.length === 0 && (
          <Text size="sm" c="dimmed" fs="italic">
            {emptyMeans}
          </Text>
        )}
        {rules.map((rule, index) => {
          const days = rule.daysOfWeek ?? [];
          return (
            // The rule is right and this is a known gap, not a false positive. The comment that
            // stood here claimed rules are "never reordered", which is not true — Remove is
            // `filter((_, i) => i !== index)`, so deleting the first of three shifts the others
            // down an index and React reuses the removed rule's DOM for the one that takes its
            // place. Every field is controlled from `rules`, so the VALUES redraw correctly; what
            // does not survive is what the DOM owns — focus, and a number input's half-typed text.
            // Keying it properly needs an id per rule that survives immutable updates, which
            // changes how this field holds state and does not belong in a commit whose whole
            // purpose is that nothing behaves differently. Recorded in docs/track-1-status.md.
            // biome-ignore lint/suspicious/noArrayIndexKey: known gap, reasoned above
            <Paper key={`rule-${index}`} withBorder p="sm" radius="sm">
              <Stack gap="xs">
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                  {/* The chips wrap and the button does not: in a narrow pane the seven days take
                      two lines, and without this the button is pushed off the edge as "Remo". */}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Chip.Group
                      multiple
                      value={days.map(String)}
                      onChange={(v) => patch(index, { daysOfWeek: v.map(Number).sort() })}
                    >
                      <Group gap={6} wrap="wrap">
                        {DAYS.map((d) => (
                          <Chip key={d.value} value={String(d.value)} size="xs" variant="outline">
                            {d.short}
                          </Chip>
                        ))}
                      </Group>
                    </Chip.Group>
                  </div>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="red"
                    style={{ flexShrink: 0 }}
                    onClick={() => set(rules.filter((_, i) => i !== index))}
                  >
                    Remove
                  </Button>
                </Group>
                <Group gap="xs" align="flex-end" wrap="wrap">
                  {/* Native time inputs: the keyboard, the 24-hour clock and the validation all
                      come from the browser, and a phone behind the bar gets its own time picker. */}
                  <TextInput
                    type="time"
                    label="From"
                    size="sm"
                    value={rule.startTime ?? ''}
                    onChange={(e) => patch(index, { startTime: e.currentTarget.value || null })}
                    style={{ width: 130 }}
                  />
                  <TextInput
                    type="time"
                    label="Until"
                    size="sm"
                    value={rule.endTime ?? ''}
                    onChange={(e) => patch(index, { endTime: e.currentTarget.value || null })}
                    style={{ width: 130 }}
                  />
                  <DateInput
                    label="Starting"
                    size="sm"
                    placeholder="Any date"
                    valueFormat="D MMM YYYY"
                    clearable
                    value={rule.startDate ?? null}
                    onChange={(v) => patch(index, { startDate: v || null })}
                    popoverProps={{ withinPortal: true }}
                    style={{ width: 160 }}
                  />
                  <DateInput
                    label="Ending"
                    size="sm"
                    placeholder="Any date"
                    valueFormat="D MMM YYYY"
                    clearable
                    value={rule.endDate ?? null}
                    onChange={(v) => patch(index, { endDate: v || null })}
                    popoverProps={{ withinPortal: true }}
                    style={{ width: 160 }}
                  />
                </Group>
                {/* Times that wrap past midnight are legitimate here — last call is 01:00 — so this
                    reads the rule back rather than refusing it. */}
                {rule.startTime && rule.endTime && rule.startTime > rule.endTime && (
                  <Text size="xs" c="dimmed">
                    Runs past midnight, into the next morning.
                  </Text>
                )}
              </Stack>
            </Paper>
          );
        })}
        <Button
          size="compact-sm"
          variant="light"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => set([...rules, { daysOfWeek: [], startTime: null, endTime: null }])}
        >
          {rules.length === 0 ? 'Set times' : 'Add another time'}
        </Button>
      </Stack>
    </div>
  );
}
